import i18n from 'i18next'
import { CORRECTIONS, DISCUSSION } from 'routes/routeConstant'
import { formatRelativeValue } from '../../i18n/format'
import { Discussion, DiscussionAnswer, DiscussionNotification, DiscussionUnread } from './discussionApi'

/** Replies show their time only while recent. Answers never do: most are months old. */
export const REPLY_TIME_DAYS = 7
export const REPLY_MAX_CHARS = 200
export const PREVIEW_DESKTOP = 3
export const PREVIEW_PHONE = 2

const HOUR = 3_600_000
const DAY = 24 * HOUR

/**
 * "just now", "2 hours ago", "yesterday", "3 days ago", or '' when older than a week. In the
 * interface language (Intl relative time); "just now" is ours.
 */
export const replyTime = (createdAt: string | Date | null | undefined, now: Date = new Date()): string => {
  if (!createdAt) return ''
  const age = now.getTime() - new Date(createdAt).getTime()
  const justNow = i18n.t('discussion:time.justNow')
  if (!(age >= 0)) return justNow
  if (age >= REPLY_TIME_DAYS * DAY) return ''
  if (age < 2 * 60_000) return justNow
  if (age < HOUR) return formatRelativeValue(-Math.floor(age / 60_000), 'minute')
  if (age < DAY) return formatRelativeValue(-Math.floor(age / HOUR), 'hour')
  return formatRelativeValue(-Math.floor(age / DAY), 'day')
}

/** The answers the page preview shows: other learners' only, in the server's order. */
export const previewAnswers = (discussion: Discussion | null, count: number): DiscussionAnswer[] =>
  (discussion?.answers || []).filter((a) => !a.mine && !a.hidden).slice(0, count)

/** "Lin and Mei replied to your answer", from the unread replies to the viewer's answer. */
export const unreadLine = (unread: DiscussionUnread[]): string => {
  const names: string[] = []
  for (const u of unread) {
    const name = u.actorIsTeacher
      ? i18n.t('discussion:unread.teacherName', { name: u.actorName || i18n.t('discussion:unread.yourTeacher') })
      : u.actorName || i18n.t('discussion:unread.aLearner')
    if (!names.includes(name)) names.push(name)
  }
  if (!names.length) return ''
  if (names.length === 1) return i18n.t('discussion:unread.one', { name: names[0] })
  if (names.length === 2) return i18n.t('discussion:unread.two', { first: names[0], second: names[1] })
  return i18n.t('discussion:unread.many', { first: names[0], count: names.length - 1 })
}

/** One line for the bell. */
export const notificationLine = (n: DiscussionNotification): string => {
  const name = n.actorName || i18n.t('discussion:notifications.aLearner')
  switch (n.kind) {
    case 'teacher_reply':
      return i18n.t('discussion:notifications.teacherReply', { name })
    case 'reply_to_answer':
      return i18n.t('discussion:notifications.replyToAnswer', { name })
    case 'reply_in_thread':
      return i18n.t('discussion:notifications.replyInThread', { name })
    case 'answer_liked':
      return n.actorIsTeacher
        ? i18n.t('discussion:notifications.teacherLikedAnswer', { name })
        : i18n.t('discussion:notifications.likedAnswer', { name })
    case 'reply_liked':
      return n.actorIsTeacher
        ? i18n.t('discussion:notifications.teacherLikedReply', { name })
        : i18n.t('discussion:notifications.likedReply', { name })
    case 'words_added':
      return i18n.t('discussion:notifications.wordsAdded', { count: n.count || 1 })
    case 'correction_ready':
      return n.actorName
        ? i18n.t('discussion:notifications.correctionReady', { name: n.actorName })
        : i18n.t('discussion:notifications.correctionReadyNoName')
    default:
      return i18n.t('discussion:notifications.other')
  }
}

/** Where a bell line opens: the corrections page for a returned correction, else the discussion. */
export const notificationPath = (n: DiscussionNotification): string =>
  n.kind === 'correction_ready' ? CORRECTIONS : discussionPath(n.storyId, n.answerRef)

/** Where a notification or email opens: the discussion page, at that answer. */
export const discussionPath = (storyId: string, answerRef?: string | null): string =>
  `${DISCUSSION}/${encodeURIComponent(storyId)}${answerRef ? `?answer=${encodeURIComponent(answerRef)}` : ''}`

/** Share of letters that must be Chinese characters, as for answers (answerValidate). */
export const MIN_CHINESE_SHARE = 0.8

export const isMostlyChinese = (text: string): boolean => {
  const chinese = (text.match(/\p{Script=Han}/gu) || []).length
  const latin = (text.match(/[a-zA-Z]/g) || []).length
  return chinese > 0 && chinese / (chinese + latin) >= MIN_CHINESE_SHARE
}

/** Shown while typing: the only thing worth saying before Send, since pinyin passes through Latin letters. */
export const replyTooLong = (text: string): string | null =>
  Array.from(text.trim()).length > REPLY_MAX_CHARS
    ? i18n.t('discussion:composer.tooLong', { max: REPLY_MAX_CHARS })
    : null

/** The server's checks, run when Send is pressed so it can say why without a round trip. */
export const replyProblem = (text: string): string | null => {
  const trimmed = text.trim()
  if (!trimmed) return null
  return replyTooLong(trimmed) || (isMostlyChinese(trimmed) ? null : i18n.t('discussion:composer.notChinese'))
}

/** The first letter of a name for its avatar. */
export const initialOf = (name: string) => (name || '?').trim().charAt(0).toUpperCase() || '?'
